import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseScopeChange } from "../../shared/types/api";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { SCOPE_CHANGE_REASONS, formatReleaseMoment } from "./releaseVocabulary";

const REASON_LABELS = new Map(SCOPE_CHANGE_REASONS);

/**
 * Every change to what this release carries, with its coded reason (§14).
 *
 * <p>§13's argument is that pretending scope never changes is worse than admitting it does: teams
 * work around a system that will not let them add an emergency fix, and then the record shows
 * nothing at all. The service has recorded this ledger since the first release was created — it
 * simply had no screen, which meant the audit trail existed and nobody could read it.
 *
 * <p>Changes made after the freeze are marked. They are the ones a release review asks about, and
 * a flat list that treated them like any other edit would make them the hardest thing to find.
 */
export function ScopeChangeLedger({ release }: { release: ReleaseDetail }) {
  const changes = useQuery({
    queryKey: ["release-scope-changes", release.id],
    queryFn: () =>
      api.get<ReleaseScopeChange[]>(`/api/v1/releases/${release.id}/scope/changes`),
  });

  const rows = changes.data ?? [];
  const afterFreeze = rows.filter((row) => row.afterScopeFreeze).length;

  return (
    <Card>
      <CardHeader
        title="Scope changes"
        subtitle={
          rows.length === 0
            ? "Nothing has moved in or out."
            : `${rows.length} ${rows.length === 1 ? "change" : "changes"}${
                afterFreeze > 0 ? ` · ${afterFreeze} after the freeze` : ""
              }`
        }
      />
      <CardBody>
        {changes.isPending ? (
          <LoadingState label="Loading the ledger…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing has moved"
            message="Every addition and removal is recorded here with the reason it was given."
          />
        ) : (
          <ul className="divide-y divide-ink-100">
            {rows.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="flex min-w-0 gap-2.5">
                  <span className="mt-0.5 shrink-0">
                    {row.changeType === "REMOVED" ? (
                      <ArrowDownLeft className="h-4 w-4 text-red-500" aria-hidden />
                    ) : (
                      <ArrowUpRight className="h-4 w-4 text-green-600" aria-hidden />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-ink-900">
                      <span className="font-mono text-xs text-ink-500">{row.itemKey}</span>{" "}
                      {row.title}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {row.changeType === "REMOVED" ? "Removed" : "Added"}
                      {" · "}
                      {REASON_LABELS.get(row.reasonCode) ?? row.reasonCode}
                      {row.changedByName && ` · ${row.changedByName}`}
                      {" · "}
                      {formatReleaseMoment(row.changedAt, release.timeZone)}
                    </p>
                    {row.comment && (
                      <p className="mt-1 text-xs text-ink-600">{row.comment}</p>
                    )}
                  </div>
                </div>
                {row.afterScopeFreeze && (
                  <StatusBadge tone="amber">After freeze</StatusBadge>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
